import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import UpInput from '../../components/Common/UpInput';
import UpButton from '../../components/Common/UpButton';
import UpCheckbox from '../../components/Common/UpCheckbox';
import AppPage from './AppPage';
import { getAddressFormat, getVatIdExample } from './addressFormat';
import {
  submitSignupIdentity,
  submitSignupDetails,
  getSignupErrorMessage,
  getSignupRequestId,
} from '../../api/signup';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const TOS_URL = 'https://www.urbanpiper.com/legal/merchant-agreement';
// Country-shaped address fields → their input name + error key here.
const ADDRESS_INPUTS = {
  city: { name: 'billing_city', errorKey: 'city' },
  state: { name: 'billing_state', errorKey: 'region' },
  zip: { name: 'billing_zip', errorKey: 'zip' },
};

// Steps 1+2 merged into ONE screen (owner's one-flow, Aug 6): the server
// still owns two transitions (identity → billing → sign), so Continue
// submits them back-to-back. If the second call fails the server simply
// rests at "billing" and this same screen resumes with everything typed.
function BusinessBillingView({ token, state, onStateChange, onBack }) {
  const [legalEntity, setLegalEntity] = useState(
    state?.identity?.legal_entity || '',
  );
  const [contactName, setContactName] = useState(
    state?.identity?.billing_contact_name ||
      [state?.first_name, state?.last_name].filter(Boolean).join(' '),
  );
  const [contactEmail, setContactEmail] = useState(
    state?.identity?.billing_contact_email || state?.email || '',
  );
  const [street, setStreet] = useState(state?.billing_address?.street || '');
  const [city, setCity] = useState(state?.billing_address?.city || '');
  const [region, setRegion] = useState(state?.billing_address?.state || '');
  const [zip, setZip] = useState(state?.billing_address?.zip || '');
  // Merchants outside the US (e.g. Wolt referrals in Europe) carry their
  // country from the referral; the address then uses region/postcode wording
  // and the region is optional. No country (older API) = US, as before.
  const country = state?.country || 'United States';
  const isUS = country === 'United States';
  // Known countries get Google's address metadata (libaddressinput): which
  // fields show, their order and words, the postcode pattern. null = US
  // (unchanged) or a name we can't resolve (the generic non-US form).
  const addressFormat = getAddressFormat(state?.country);
  const addressValues = {
    city: [city, setCity],
    state: [region, setRegion],
    zip: [zip, setZip],
  };

  // A field the country's format hides is sent blank, never a stale value.
  function shownValue(field) {
    return addressFormat && !addressFormat.fields[field]
      ? ''
      : addressValues[field][0].trim();
  }
  // Optional VAT ID (reverse-charge invoicing) — only when Atlance offers it
  // for this merchant; absent flag (US, older API) = no field at all.
  const vatIdSupported = Boolean(state?.vat_id_supported);
  // The example follows the country (EL… for Greece, ATU… for Austria).
  const vatIdExample = getVatIdExample(state?.country);
  const [vatId, setVatId] = useState(state?.vat_id || '');
  // Already-recorded agreement stays checked on a resume.
  const [tosAgreed, setTosAgreed] = useState(Boolean(state?.tos_agreed));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const { isLoading: loading, mutate: saveStep } = useMutation({
    mutationFn: async () => {
      const afterIdentity = await submitSignupIdentity(token, {
        legal_entity: legalEntity.trim(),
        billing_contact_name: contactName.trim(),
        billing_contact_email: contactEmail.trim(),
        // Recorded server-side with a timestamp (tos_agreed_at).
        tos_agreed: tosAgreed,
      });
      if (afterIdentity?.step !== 'billing') {
        return afterIdentity;
      }
      // Format only — the server checks the number itself (python-stdnum).
      const normalizedVatId = vatId.replace(/\s+/g, '').toUpperCase();
      if (vatIdSupported) {
        setVatId(normalizedVatId);
      }
      const details = {
        billing_street: street.trim(),
        billing_city: shownValue('city'),
        billing_state: shownValue('state'),
        billing_zip: shownValue('zip'),
        // Blank is sent too, so a prefilled VAT ID can be cleared.
        ...(vatIdSupported && { vat_id: normalizedVatId }),
      };
      try {
        return await submitSignupDetails(token, details);
      } catch (err) {
        err.details = details;
        throw err;
      }
    },
    onSuccess: onStateChange,
    onError: err => {
      // With street, city and postcode all sent (the India rail's only other
      // 422s), a 422 on the request carrying a VAT ID rejects that VAT ID.
      const sent = err.details;
      if (
        err.response?.status === 422 &&
        sent?.vat_id &&
        sent.billing_street &&
        sent.billing_city &&
        sent.billing_zip
      ) {
        setFieldErrors(prev => ({
          ...prev,
          vatId: getSignupErrorMessage(err),
        }));
        return;
      }
      setSubmitError(err);
    },
  });

  function validate() {
    const errors = {};

    if (!legalEntity.trim()) {
      errors.legalEntity = 'Please enter your registered business name';
    }

    if (!contactName.trim()) {
      errors.contactName = 'Please enter the billing contact name';
    }

    if (!EMAIL_REGEX.test(contactEmail.trim())) {
      errors.contactEmail = 'Please enter a valid email address';
    }

    if (!street.trim()) {
      errors.street = 'Please enter your street address';
    }

    if (addressFormat) {
      Object.entries(addressFormat.fields).forEach(([field, spec]) => {
        const value = addressValues[field][0].trim();
        const { errorKey } = ADDRESS_INPUTS[field];
        if (spec.required && !value) {
          errors[errorKey] = `Please enter your ${spec.word}`;
        } else if (
          field === 'zip' &&
          value &&
          addressFormat.zipPattern &&
          !addressFormat.zipPattern.test(value)
        ) {
          errors[errorKey] = `Please enter a valid ${spec.word}`;
        }
      });
    } else {
      if (!city.trim()) {
        errors.city = 'Please enter your city';
      }

      if (isUS && !region.trim()) {
        errors.region = 'Please enter your state';
      }

      if (!zip.trim()) {
        errors.zip = isUS ? 'Please enter your ZIP code' : 'Please enter your postcode';
      }
    }

    if (!tosAgreed) {
      errors.tosAgreed = 'Please agree to the Terms of Service to continue';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);

    if (loading || !validate()) {
      return;
    }

    saveStep();
  }

  function onFieldChange(field, setter) {
    return value => {
      setSubmitError(null);
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
      setter(value);
    };
  }

  function renderAddressInput(field) {
    const { label, required } = addressFormat.fields[field];
    const { name, errorKey } = ADDRESS_INPUTS[field];
    const [value, setter] = addressValues[field];
    return (
      <UpInput
        key={field}
        type="text"
        name={name}
        wrapperClass="mt-5 flex-1"
        label={required ? label : `${label} (optional)`}
        value={value}
        onChange={onFieldChange(errorKey, setter)}
        errorContent={fieldErrors[errorKey]}
      />
    );
  }

  return (
    <AppPage step={state?.step}
      noPayment={state?.payment_at_signup === 'invoice'} loading={loading}>
      <h1 className="signup-title">Business &amp; Billing</h1>
      <p className="signup-sub">
        Hi {state?.first_name || 'there'}, let&apos;s set up{' '}
        {state?.business || 'your brand'} on UrbanPiper.
      </p>
      {onBack && (
        <button
          type="button"
          className="text-sm text-up-blue underline -mt-5 mb-5 block"
          onClick={onBack}>
          ← Edit contact details
        </button>
      )}
      <form onSubmit={handleSubmit}>
        <UpInput
          autoFocus
          type="text"
          name="legal_entity"
          label="Legal entity name"
          placeholder="Registered business name on your agreement"
          value={legalEntity}
          onChange={onFieldChange('legalEntity', setLegalEntity)}
          errorContent={fieldErrors.legalEntity}
        />
        <UpInput
          type="text"
          name="billing_contact_name"
          wrapperClass="mt-5"
          label="Billing contact name"
          placeholder="Ex: Jordan Smith"
          value={contactName}
          onChange={onFieldChange('contactName', setContactName)}
          errorContent={fieldErrors.contactName}
        />
        <UpInput
          type="text"
          name="billing_contact_email"
          wrapperClass="mt-5"
          label="Billing contact email"
          placeholder="Ex: billing@example.com"
          helpContent="Invoices go here. Signup emails keep going to your contact email."
          value={contactEmail}
          onChange={onFieldChange('contactEmail', setContactEmail)}
          errorContent={fieldErrors.contactEmail}
        />
        {!isUS && (
          <div className="mt-5">
            <div className="signup-readonly-label">Country</div>
            <div className="signup-readonly-value">{country}</div>
          </div>
        )}
        <UpInput
          type="text"
          name="billing_street"
          wrapperClass="mt-5"
          label="Street address"
          placeholder={isUS ? 'Ex: 500 Palm Avenue, Suite 12' : 'Street and number'}
          value={street}
          onChange={onFieldChange('street', setStreet)}
          errorContent={fieldErrors.street}
        />
        {addressFormat ? (
          addressFormat.rows.map(row => (
            <div className="flex gap-4" key={row.join('-')}>
              {row.map(renderAddressInput)}
            </div>
          ))
        ) : (
          <>
            <div className="flex gap-4">
              <UpInput
                type="text"
                name="billing_city"
                wrapperClass="mt-5 flex-1"
                label="City"
                placeholder={isUS ? 'Ex: Miami' : undefined}
                value={city}
                onChange={onFieldChange('city', setCity)}
                errorContent={fieldErrors.city}
              />
              <UpInput
                type="text"
                name="billing_state"
                wrapperClass="mt-5 flex-1"
                label={isUS ? 'State' : 'State / region (optional)'}
                placeholder={isUS ? 'Ex: FL' : undefined}
                value={region}
                onChange={onFieldChange('region', setRegion)}
                errorContent={fieldErrors.region}
              />
            </div>
            <UpInput
              type="text"
              name="billing_zip"
              wrapperClass="mt-5"
              label={isUS ? 'ZIP code' : 'Postcode'}
              placeholder={isUS ? 'Ex: 33101' : undefined}
              value={zip}
              onChange={onFieldChange('zip', setZip)}
              errorContent={fieldErrors.zip}
            />
          </>
        )}
        {vatIdSupported && (
          <UpInput
            type="text"
            name="vat_id"
            wrapperClass="mt-5"
            label="VAT ID (optional)"
            helpContent={
              vatIdExample
                ? `For reverse-charge invoicing — e.g. ${vatIdExample}`
                : 'Your EU VAT number, for reverse-charge invoicing'
            }
            value={vatId}
            onChange={onFieldChange('vatId', setVatId)}
            errorContent={fieldErrors.vatId}
          />
        )}
        <UpCheckbox
          name="tos_agreed"
          wrapperClass="mt-5 mb-8"
          value={tosAgreed}
          onChange={onFieldChange('tosAgreed', setTosAgreed)}
          errorContent={fieldErrors.tosAgreed}
          label={
            <span>
              By signing up, I agree to the UrbanPiper{' '}
              <a
                href={TOS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-up-blue underline"
                onClick={e => e.stopPropagation()}>
                Terms of Service
              </a>
              .
            </span>
          }
        />
        {submitError && (
          <div className="text-sm text-up-clr-semantic-red break-words mb-6">
            {getSignupErrorMessage(submitError)}
            {getSignupRequestId(submitError) && (
              <span className="block text-xs text-up-clr-light-grey mt-1">
                Reference: {getSignupRequestId(submitError)}
              </span>
            )}
          </div>
        )}
        <UpButton type="submit" disabled={loading}>
          {loading ? 'Saving…' : 'Continue'}
        </UpButton>
        <p className="signup-footnote">
          Your order form opens for signing in the next step.
        </p>
      </form>
    </AppPage>
  );
}

export default BusinessBillingView;
